import { useEffect, useState } from 'react';

import {
  useMediaQuery,
  Table,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ReferenceLine,
  Tooltip,
  Cell,
} from 'recharts';

import Text from '../../elements/text/Text';
import NoContent from '../../sections/no-content/NoContent';

const StyledTooltip = styled('div')({
  display: 'flex',
  justify: 'center',
  alignItems: 'center',
  backgroundColor: 'black',
  opacity: '0.8',
  borderRadius: 5,
  color: 'white',
  padding: '2px 10px 2px 10px',
});

const StyledIndent = styled('div')(({ theme }) => ({
  marginLeft: theme.spacing(2),
  marginTop: theme.spacing(1),
}));

const S3BarChart = ({ getStorage }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const [storage, setStorage] = useState(null);

  const fetchStorage = async () => {
    const storageRes = await getStorage();
    setStorage(storageRes);
  };

  useEffect(() => {
    fetchStorage();
  }, []);

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <section>
      <Text>{t('backupDataUse', { ns: 'sectionTitles' })}</Text>
      {!storage ? (
        <NoContent title={t('noDataUsage', { ns: 'descriptions' })} />
      ) : isSmallScreen ? (
        <StyledIndent>
          <Table size="small">
            <TableBody>
              {storage.usage.map((entry) => {
                return (
                  <TableRow key={entry.name}>
                    <TableCell>{entry.name}:</TableCell>
                    <TableCell>{entry.value} GB</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </StyledIndent>
      ) : (
        <div data-testid="s3-barchart">
          <StyledIndent>
            <BarChart
              width={600}
              height={120}
              data={storage.usage}
              layout="vertical"
              margin={{ left: 50 }}
            >
              <XAxis type="number" domain={[0, 'auto']} />
              <YAxis
                type="category"
                dataKey="name"
                tickLine={false}
                width={120}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" barSize={20} fill={theme.palette.info.main}>
                {storage.usage.map((entry, index) => {
                  return (
                    <Cell
                      key={index}
                      fill={
                        entry.value > storage.quota
                          ? theme.palette.error.light
                          : theme.palette.info.light
                      }
                    />
                  );
                })}
              </Bar>
              <ReferenceLine x={storage.quota} />
            </BarChart>
          </StyledIndent>
        </div>
      )}
    </section>
  );
};

S3BarChart.propTypes = {
  getStorage: PropTypes.func,
};

export default S3BarChart;

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <StyledTooltip>
        <Text caption>
          {label}: {payload[0].value} GB
        </Text>
      </StyledTooltip>
    );
  }
  return null;
};

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  label: PropTypes.string,
};
